import React from "react";
import { ArrowUpRight, MapPin, ShieldCheck, Sparkles, Wallet, FlaskConical } from "lucide-react";

const FEATURES = [
  {
    Icon: MapPin,
    label: "Designed in the USA",
    body: "ViewLux is designed and engineered in Pennsylvania &mdash; combining American craftsmanship with the world&rsquo;s most respected window and door technologies.",
  },
  {
    Icon: ShieldCheck,
    label: "LoĒ² Glass · Tempered",
    body: "Every unit ships with tempered, dual-coated LoĒ² glass &mdash; a microscopic silver coating that reflects heat back inside in winter, deflects it outside in summer, and blocks up to 84% of UV rays that fade interiors.",
    cta: { label: "Learn about LoĒ²", href: "https://www.cardinalcorp.com/products/glass/loe-glass/loe-2-272-glass/" },
  },
  {
    Icon: Sparkles,
    label: "Veteran Engineering",
    body: "Our engineers bring decades of industry experience to every system &mdash; designing products that compete with (and outperform) the biggest names on the market.",
  },
  {
    Icon: Wallet,
    label: "Factory-Direct Pricing",
    body: "We sell direct from our showrooms across the United States. No dealer markups, no middlemen &mdash; just refined engineering at an honest price.",
  },
  {
    Icon: FlaskConical,
    label: "Independently Tested",
    body: "Every ViewLux product is put through rigorous, third-party performance testing &mdash; structural load, water infiltration, thermal cycling, and air-tightness &mdash; at <strong>Molimo Labs</strong>, an independent building-product testing facility. Nothing leaves our line without certified test data backing it up.",
    cta: { label: "Visit Molimo Labs", href: "https://molimo.com/" },
    wide: true,
  },
];

export default function OurStory() {
  return (
    <section
      data-testid="our-story"
      id="story"
      className="relative bg-cream"
    >
      <div className="container-vlx py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-start">
          {/* Left column — narrative */}
          <div className="md:col-span-5 md:sticky md:top-32">
            <span className="eyebrow">Our Story</span>
            <div className="mt-4 rule-gold" />
            <h2 className="heading-lg mt-8 text-[34px] md:text-[52px] leading-[1.08] text-[#0f2420]">
              American engineering.{" "}
              <em className="italic font-light text-[#7a5b2f]">
                Global aluminum technology.
              </em>
            </h2>
            <p className="mt-8 text-neutral-700 text-[15px] leading-[1.95] font-nunito max-w-md">
              ViewLux Windows &amp; Doors is designed and engineered in{" "}
              <strong className="text-[#0f2420] font-semibold">Pennsylvania, USA</strong>{" "}
              &mdash; built around the conviction that a window can be both
              refined and accessible. We source the world&rsquo;s best hardware,
              specify premium tempered glass, and sell direct to homeowners,
              architects, and builders from showrooms across the country.
            </p>
            <p className="mt-6 text-neutral-700 text-[15px] leading-[1.95] font-nunito max-w-md">
              The result: thermally broken aluminum systems engineered to
              compete with the most established brands in the industry &mdash;
              at a price that respects your budget.
            </p>
          </div>

          {/* Right column — feature grid */}
          <div className="md:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-px bg-[#c99658]/25">
              {FEATURES.map((f, i) => (
                <li
                  key={f.label}
                  data-testid={`our-story-card-${i}`}
                  className={`group bg-cream p-8 md:p-10 transition-colors duration-500 hover:bg-white ${f.wide ? "sm:col-span-2" : ""}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-[#c99658]/40 text-[#7a5b2f] group-hover:border-[#c99658] group-hover:bg-[#c99658] group-hover:text-white transition-colors">
                      <f.Icon className="w-4 h-4" strokeWidth={1.6} />
                    </span>
                    <span className="text-[10.5px] tracking-[0.4em] uppercase font-raleway text-[#7a5b2f]">
                      0{i + 1}
                    </span>
                    <span className="flex-1 h-px bg-[#c99658]/30" />
                  </div>

                  <h3 className="heading-lg mt-6 text-[22px] md:text-[26px] text-[#0f2420] leading-[1.15]">
                    {f.label}
                  </h3>
                  <p
                    className="mt-4 text-neutral-700 text-[14px] leading-[1.85] font-nunito"
                    dangerouslySetInnerHTML={{ __html: f.body }}
                  />

                  {f.cta ? (
                    <a
                      href={f.cta.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`our-story-cta-${i}`}
                      className="link-arrow mt-5 inline-flex"
                    >
                      {f.cta.label}
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
